import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser, UserButton } from "@clerk/clerk-react";
import { Helmet } from "react-helmet-async";
import { Menu, User2, X } from "lucide-react";
import CartButton from "../cart/CartButton";
import { useAuthStatus } from "../auth/AuthProvider";
import {
  initializeAmplitude,
  trackEvent,
  identifyUser,
  testAmplitudeConnection,
} from "../../utils/amplitude";
import { normalizeCustomLandingContext } from "../../utils/customLanding";
import { devLog } from "../../utils/devLog";

interface LayoutProps {
  children: React.ReactNode;
}

const CUSTOM_ROUTE_LAYOUT_COPY = {
  en: {
    browseCases: "Browse Cases",
    customCases: "Custom Cases",
    quiz: "Quiz",
    myAgency: "My Agency",
    new: "New",
    account: "Account",
    tagline: "Interactive murder mystery games for detectives at heart.",
    explore: "Explore",
    whatsInside: "What's Inside",
    howItWorks: "How It Works",
    company: "Company",
    blog: "Blog",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy",
    refund: "Refund",
    terms: "Terms",
    disclaimer: "Disclaimer",
    rights: "All rights reserved.",
  },
  fr: {
    browseCases: "Voir les affaires",
    customCases: "Affaires personnalisees",
    quiz: "Quiz",
    myAgency: "Mon Agence",
    new: "Nouveau",
    account: "Compte",
    tagline: "Jeux de mystere interactifs pour detectives dans l'ame.",
    explore: "Explorer",
    whatsInside: "Contenu",
    howItWorks: "Comment ca marche",
    company: "Entreprise",
    blog: "Blog",
    contact: "Contact",
    legal: "Mentions legales",
    privacy: "Confidentialite",
    refund: "Remboursement",
    terms: "Conditions",
    disclaimer: "Avertissement",
    rights: "Tous droits reserves.",
  },
  es: {
    browseCases: "Ver casos",
    customCases: "Casos personalizados",
    quiz: "Quiz",
    myAgency: "Mi Agencia",
    new: "Nuevo",
    account: "Cuenta",
    tagline: "Juegos interactivos de misterio para detectives de corazon.",
    explore: "Explorar",
    whatsInside: "Que incluye",
    howItWorks: "Como funciona",
    company: "Empresa",
    blog: "Blog",
    contact: "Contacto",
    legal: "Legal",
    privacy: "Privacidad",
    refund: "Reembolso",
    terms: "Terminos",
    disclaimer: "Aviso legal",
    rights: "Todos los derechos reservados.",
  },
  de: {
    browseCases: "Falle ansehen",
    customCases: "Individuelle Falle",
    quiz: "Quiz",
    myAgency: "Meine Agentur",
    new: "Neu",
    account: "Konto",
    tagline: "Interaktive Krimispiele fur Detektivinnen und Detektive.",
    explore: "Entdecken",
    whatsInside: "Inhalt",
    howItWorks: "So funktioniert es",
    company: "Unternehmen",
    blog: "Blog",
    contact: "Kontakt",
    legal: "Rechtliches",
    privacy: "Datenschutz",
    refund: "Erstattung",
    terms: "Bedingungen",
    disclaimer: "Haftungsausschluss",
    rights: "Alle Rechte vorbehalten.",
  },
  it: {
    browseCases: "Esplora i casi",
    customCases: "Casi personalizzati",
    quiz: "Quiz",
    myAgency: "La mia Agenzia",
    new: "Nuovo",
    account: "Account",
    tagline: "Giochi investigativi interattivi per detective nell'anima.",
    explore: "Esplora",
    whatsInside: "Cosa include",
    howItWorks: "Come funziona",
    company: "Azienda",
    blog: "Blog",
    contact: "Contatto",
    legal: "Note legali",
    privacy: "Privacy",
    refund: "Rimborso",
    terms: "Termini",
    disclaimer: "Disclaimer",
    rights: "Tutti i diritti riservati.",
  },
} as const;

const Layout = ({ children }: LayoutProps) => {
  const { isSignedIn, user } = useUser();
  const { isAdmin } = useAuthStatus();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const location = useLocation();
  const isAgencyRoute = location.pathname === "/agency" || location.pathname.startsWith("/agency/");
  const isCustomRoute = location.pathname === "/custom" || location.pathname.startsWith("/custom/");
  const customRouteLang = isCustomRoute
    ? normalizeCustomLandingContext(new URLSearchParams(location.search)).lang
    : "en";
  const layoutCopy = CUSTOM_ROUTE_LAYOUT_COPY[customRouteLang];
  const isAgencyAuthRoute =
    location.pathname === "/agency/sign-in" || location.pathname === "/agency/sign-up";
  const isAgencyCaseRoute = location.pathname.startsWith("/agency/cases/");
  const agencySearchParams = new URLSearchParams(location.search);
  const isRegisteredDemoCaseRoute =
    isAgencyCaseRoute && agencySearchParams.get("demo") === "registered";
  const registeredDemoStepKey = String(agencySearchParams.get("demoStep") || "").trim() || "free_case";
  const registeredDemoReturnStepKey =
    registeredDemoStepKey === "free_case" ? "archive_preview" : registeredDemoStepKey;
  const agencyLogoSrc = "/images/agency_logo.png";
  const defaultFaviconSrc = "/logo.png";
  const agencyHeaderTitle = (() => {
    if (location.pathname.startsWith("/agency/cases/")) return "Case View";
    if (location.pathname.startsWith("/agency/archive/")) return "Archive View";
    if (location.pathname.startsWith("/agency/settings")) return "Account";
    return "Member Dashboard";
  })();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Track scroll for header blur effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // TODO: remove devMode gate when My Club is ready for all users
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("devmode") === "1") {
      localStorage.setItem("devmode", "1");
      params.delete("devmode");
      const newUrl = [window.location.pathname, params.toString()].filter(Boolean).join("?");
      window.history.replaceState(null, "", newUrl);
    }
    setDevMode(localStorage.getItem("devmode") === "1");
  }, []);

  const isClubNavEnabled =
    import.meta.env.VITE_CLUB_ENABLED === "true" && (devMode || import.meta.env.DEV);
  const accountHref = isSignedIn ? "/agency/settings" : "/agency/sign-in";
  const agencyHref = isSignedIn ? "/agency/dashboard" : "/agency";
  const agencyNavHref = isRegisteredDemoCaseRoute
    ? `/agency/dashboard?demo=registered&demoStep=${encodeURIComponent(
        registeredDemoReturnStepKey
      )}#registered-demo`
    : agencyHref;
  const agencyNavLabel = isRegisteredDemoCaseRoute ? "Back to Demo" : "Dashboard";

  // Initialize Amplitude once when component mounts
  useEffect(() => {
    console.log("Initializing Amplitude...");
    initializeAmplitude();
  }, []);

  // Identify user when signed in
  useEffect(() => {
    if (isSignedIn && user) {
      console.log("User signed in, identifying in Amplitude...");
      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        is_admin: isAdmin,
      });
    }
  }, [isSignedIn, user, isAdmin]);

  // Track page views when location changes
  useEffect(() => {
    const pageName = getPageNameFromPath(location.pathname);

    console.log(`Tracking page view: ${pageName}`);
    trackEvent("Page View", {
      page: pageName,
      path: location.pathname,
      referrer: document.referrer,
    });
  }, [location.pathname]);

  useEffect(() => {
    const faviconHref = isAgencyRoute ? agencyLogoSrc : defaultFaviconSrc;
    const faviconLinks = [
      { rel: "icon", type: "image/png" },
      { rel: "shortcut icon", type: "image/png" },
      { rel: "apple-touch-icon" },
    ];

    faviconLinks.forEach(({ rel, type }) => {
      let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      if (type) {
        link.type = type;
      }
      link.href = faviconHref;
    });

    devLog("navigation", "favicon_updated", {
      path: location.pathname,
      isAgencyRoute,
      faviconHref,
    });
  }, [agencyLogoSrc, defaultFaviconSrc, isAgencyRoute, location.pathname]);

  // Helper function to get page name from path
  const getPageNameFromPath = (path: string): string => {
    if (path === "/") return "Home";
    if (path.startsWith("/stories") && path.length > 8) return "Story Detail";
    if (path === "/stories") return "Browse Stories";
    if (path === "/custom") return "Create Custom";
    if (path === "/faq") return "FAQ";
    if (path === "/account" || path === "/agency/settings") return "Account";
    if (path === "/admin") return "Admin";
    if (path === "/privacy") return "Privacy Policy";
    if (path === "/terms") return "Terms of Service";

    // Extract the route part without params
    const basePath = path.split("/")[1];
    return basePath.charAt(0).toUpperCase() + basePath.slice(1);
  };

  // Track navigation clicks
  const trackNavigation = (destination: string) => {
    trackEvent("Navigation Click", {
      destination: destination,
      current_page: getPageNameFromPath(location.pathname),
    });
  };

  const handleAgencyMarketingReturn = () => {
    trackNavigation("ColdCaseGameKits.com");

    devLog("navigation", "agency_marketing_return_clicked", {
      path: location.pathname,
      returnHref: "https://coldcasegamekits.com",
      returnMode: "external_new_tab",
    });
  };

  const agencyUserButton = (
    <UserButton afterSignOutUrl="/agency">
      <UserButton.MenuItems>
        <UserButton.Link
          href="/agency/settings"
          label="Agency Account"
          labelIcon={<User2 className="h-4 w-4" />}
        />
        <UserButton.Action label="manageAccount" />
      </UserButton.MenuItems>
    </UserButton>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {isAgencyRoute && (
        <Helmet>
          <link rel="icon" type="image/png" href={agencyLogoSrc} />
          <link rel="shortcut icon" type="image/png" href={agencyLogoSrc} />
          <link rel="apple-touch-icon" href={agencyLogoSrc} />
        </Helmet>
      )}

      {/* Header */}
      {!isAgencyRoute && (
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md border-b border-border shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-6xl items-center px-6 py-4">
          {/* Logo — fixed width left section */}
          <div className="flex-1">
            <a
              href="https://coldcasegamekits.com"
              className="inline-flex items-center gap-3"
              onClick={() => trackNavigation("Home")}
            >
              <img
                src="/logo.png"
                alt="Cold Case Game Kits"
                className="h-10 w-10 rounded-full"
              />
              <span className="font-heading text-lg font-semibold tracking-tight text-foreground hidden sm:block">
                Cold Case Game Kits
              </span>
            </a>
          </div>

          {/* Center nav — true center (ul/li + list-none p-0 m-0 to match landing exactly) */}
          <ul className="hidden lg:flex items-center gap-6 list-none p-0 m-0">
            <li>
              <Link
                to="/stories"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => trackNavigation("Browse Cases")}
              >
                {layoutCopy.browseCases}
              </Link>
            </li>
            <li>
              <a
                href="https://coldcasegamekits.com/how-custom-works"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => trackNavigation("Custom Cases")}
              >
                {layoutCopy.customCases}
              </a>
            </li>
            <li>
              <a
                href="https://coldcasegamekits.com/quiz"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => trackNavigation("Quiz")}
              >
                {layoutCopy.quiz}
              </a>
            </li>
            {isAdmin ? (
              <li>
                <Link
                  to="/admin"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Admin")}
                >
                  Admin
                </Link>
              </li>
            ) : null}
            {isClubNavEnabled ? (
              <li>
                <Link
                  to={agencyHref}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("My Agency")}
                >
                  <span>{layoutCopy.myAgency}</span>
                  <span className="rounded-full border border-[#8c7340] bg-[#2a2212] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#ead8ab]">
                    {layoutCopy.new}
                  </span>
                </Link>
              </li>
            ) : null}
          </ul>

          {/* Utility actions */}
          <div className="flex-1 flex justify-end items-center gap-3">
            <Link
              to={accountHref}
              className="hidden lg:inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => trackNavigation("Account")}
            >
              {layoutCopy.account}
            </Link>

            {/* Cart Button */}
            <CartButton />

            <button
              onClick={() => {
                toggleMenu();
                trackEvent("Mobile Menu Toggle", {
                  action: isMenuOpen ? "close" : "open",
                });
              }}
              className="lg:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-background/98 backdrop-blur-lg border-t border-border">
            <div className="px-6 py-6 space-y-4">
              <Link
                to="/stories"
                className="block text-base text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  setIsMenuOpen(false);
                  trackNavigation("Browse Cases");
                }}
              >
                {layoutCopy.browseCases}
              </Link>
              <a
                href="https://coldcasegamekits.com/how-custom-works"
                className="block text-base text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  setIsMenuOpen(false);
                  trackNavigation("Custom Cases");
                }}
              >
                {layoutCopy.customCases}
              </a>
              <a
                href="https://coldcasegamekits.com/quiz"
                className="block text-base text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  setIsMenuOpen(false);
                  trackNavigation("Quiz");
                }}
              >
                {layoutCopy.quiz}
              </a>
              {isAdmin ? (
                <Link
                  to="/admin"
                  className="block text-base text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => {
                    setIsMenuOpen(false);
                    trackNavigation("Admin");
                  }}
                >
                  Admin
                </Link>
              ) : null}
              {isClubNavEnabled ? (
                <Link
                  to={agencyHref}
                  className="flex items-center justify-between text-base text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => {
                    setIsMenuOpen(false);
                    trackNavigation("My Agency");
                  }}
                >
                  <span>{layoutCopy.myAgency}</span>
                  <span className="rounded-full border border-[#8c7340] bg-[#2a2212] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#ead8ab]">
                    {layoutCopy.new}
                  </span>
                </Link>
              ) : null}
              <div className="flex items-center justify-between border-t border-border pt-4">
                <Link
                  to={accountHref}
                  className="block text-base text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => {
                    setIsMenuOpen(false);
                    trackNavigation("Account");
                  }}
                >
                  {layoutCopy.account}
                </Link>
                <CartButton />
              </div>
            </div>
          </div>
        )}
      </header>
      )}

      {isAgencyRoute && (
        <header className="sticky top-0 z-40 border-b border-[#252933] bg-[#080b12]/95 backdrop-blur-md">
          <nav className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Link
                to="/agency/dashboard"
                className="flex min-w-0 flex-1 items-center gap-3"
                onClick={() => trackNavigation("Agency Dashboard")}
              >
                <img
                  src={agencyLogoSrc}
                  alt="Cold Case Agency"
                  className="h-10 w-10 shrink-0 object-cover drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)] sm:h-11 sm:w-11"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[10px] uppercase tracking-[0.24em] text-[#c6b894] sm:text-[11px] sm:tracking-[0.3em]">
                    Cold Case Agency
                  </span>
                  <span className="block truncate text-sm font-semibold leading-tight text-[#efede7] sm:text-base">
                    {agencyHeaderTitle}
                  </span>
                </span>
              </Link>
              <div className="hidden min-w-0 shrink text-[9px] uppercase tracking-[0.14em] text-[#a7a298] sm:block sm:text-[10px] sm:tracking-[0.18em]">
                by{" "}
                <a
                  href="https://coldcasegamekits.com"
                  className="transition hover:text-[#efede7]"
                  onClick={handleAgencyMarketingReturn}
                >
                  ColdCaseGameKits.com
                </a>
              </div>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2">
              {isSignedIn ? (
                <>
                  <Link
                    to="/agency/custom-cases"
                    className="rounded-full border border-[#343845] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#dfd8c5] transition hover:bg-white/5 sm:px-4 sm:text-[11px] sm:tracking-[0.16em]"
                    onClick={() => trackNavigation("My Custom Stories")}
                  >
                    Custom Stories
                  </Link>
                  <Link
                    to={agencyNavHref}
                    className="rounded-full border border-[#343845] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#dfd8c5] transition hover:bg-white/5 sm:px-4 sm:text-[11px] sm:tracking-[0.16em]"
                    onClick={() => trackNavigation("My Agency")}
                  >
                    {agencyNavLabel}
                  </Link>
                  {agencyUserButton}
                </>
              ) : (
                <Link
                  to="/agency"
                  className="rounded-full border border-[#343845] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#dfd8c5] transition hover:bg-white/5 sm:text-[11px] sm:tracking-[0.16em]"
                  onClick={() => trackNavigation("Agency Home")}
                >
                  Agency Home
                </Link>
              )}
            </div>
          </nav>
        </header>
      )}

      {/* Main Content */}
      <main
        className={
          isAgencyRoute
            ? "flex-1 w-full"
            : "flex-1 mx-auto w-full max-w-6xl px-6 py-8 pt-24"
        }
      >
        {children}
      </main>

      {/* Footer */}
      {!isAgencyRoute && (
      <footer className="mt-auto border-t border-border bg-surface">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/logo.png"
                  alt="Cold Case Game Kits"
                  className="h-10 w-10 rounded-full"
                />
                <span className="font-heading font-semibold tracking-tight">Cold Case Game Kits</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {layoutCopy.tagline}
              </p>
            </div>

            {/* Explore */}
            <div>
              <h3 className="text-sm font-semibold mb-4 text-gold">{layoutCopy.explore}</h3>
              <nav className="flex flex-col space-y-3">
                <a
                  href="https://coldcasegamekits.com/how-custom-works"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Custom Cases")}
                >
                  {layoutCopy.customCases}
                </a>
                <a
                  href="https://coldcasegamekits.com/#whats-inside"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("What's Inside")}
                >
                  {layoutCopy.whatsInside}
                </a>
                <a
                  href="https://coldcasegamekits.com/#how-it-works"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("How It Works")}
                >
                  {layoutCopy.howItWorks}
                </a>
                <a
                  href="https://coldcasegamekits.com/quiz"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Quiz")}
                >
                  {layoutCopy.quiz}
                </a>
              </nav>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-sm font-semibold mb-4 text-gold">{layoutCopy.company}</h3>
              <nav className="flex flex-col space-y-3">
                <a
                  href="https://coldcasegamekits.com/blog"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Blog")}
                >
                  {layoutCopy.blog}
                </a>
                <a
                  href="https://coldcasegamekits.com/contact"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Contact")}
                >
                  {layoutCopy.contact}
                </a>
                <p className="text-sm text-muted-foreground">
                  contact@dgfdigital.com
                </p>
              </nav>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold mb-4 text-gold">{layoutCopy.legal}</h3>
              <nav className="flex flex-col space-y-3">
                <Link
                  to="/privacy"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Privacy")}
                >
                  {layoutCopy.privacy}
                </Link>
                <Link
                  to="/refund"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Refund")}
                >
                  {layoutCopy.refund}
                </Link>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Terms")}
                >
                  {layoutCopy.terms}
                </Link>
                <Link
                  to="/disclaimer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => trackNavigation("Disclaimer")}
                >
                  {layoutCopy.disclaimer}
                </Link>
              </nav>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 text-center text-xs text-muted-foreground">
            © 2025 Cold Case Game Kits, a division of DGF Digital Inc. {layoutCopy.rights}
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};

export default Layout;
